import React, { useRef } from 'react';
import { Form as FinalForm, Field } from 'react-final-form';
import classNames from 'classnames';

import { useIntl } from '../../../../util/reactIntl';
import { isMainSearchTypeKeywords } from '../../../../util/search';

import { Form, LocationAutocompleteInput } from '../../../../components';

import MainSearchSuggest from '../../../SearchPage/MainSearchSuggest/MainSearchSuggest';

import IconSearchDesktop from './IconSearchDesktop';
import css from './TopbarSearchForm.module.css';

const identity = v => v;

const KeywordSearchField = props => {
  const {
    keywordSearchWrapperClasses,
    iconClass,
    intl,
    isMobile = false,
    inputRef,
    submitRef,
    value,
    onChange,
    onSubmitKeywords,
    onSubmitSeller,
    onSuggestionsOpenChange,
  } = props;
  const fieldId = isMobile ? 'keyword-search-mobile' : 'keyword-search';
  return (
    <div className={keywordSearchWrapperClasses}>
      <MainSearchSuggest
        id={fieldId}
        inputId={fieldId}
        value={value}
        onChange={onChange}
        onSubmitKeywords={onSubmitKeywords}
        onSubmitSeller={onSubmitSeller}
        onSuggestionsOpenChange={onSuggestionsOpenChange}
        submitRef={submitRef}
        inputRef={inputRef}
        placeholder={intl.formatMessage({
          id: isMobile ? 'TopbarSearchForm.mobilePlaceholder' : 'TopbarSearchForm.placeholder',
        })}
        ariaLabel={intl.formatMessage({ id: 'TopbarDesktop.screenreader.search' })}
        inputClassName={isMobile ? css.mobileInput : css.desktopInput}
        rootClassName={css.keywordSearchSuggest}
        leading={
          <button
            className={css.searchSubmit}
            aria-label={intl.formatMessage({ id: 'TopbarDesktop.screenreader.search' })}
          >
            <div className={iconClass}>
              <IconSearchDesktop />
            </div>
          </button>
        }
      />
    </div>
  );
};
const SubmitButton = props => {
  const intl = useIntl();
  return (
    <button
      className={css.searchSubmit}
      aria-label={intl.formatMessage({ id: 'TopbarDesktop.screenreader.search' })}
      type="submit"
      {...props}
    >
      <IconSearchDesktop />
    </button>
  );
};

const LocationSearchField = props => {
  const { desktopInputRootClass, intl, isMobile = false, inputRef, onLocationChange } = props;
  return (
    <Field
      name="location"
      format={identity}
      render={({ input, meta }) => {
        const { onChange, ...restInput } = input;

        // Merge the standard onChange function with custom behaviur. A better solution would
        // be to use the FormSpy component from Final Form and pass onChange to the
        // onChange prop but that breaks due to insufficient subscription handling.
        // See: https://github.com/final-form/react-final-form/issues/159
        const searchOnChange = value => {
          onChange(value);
          onLocationChange(value);
        };

        return (
          <LocationAutocompleteInput
            id={isMobile ? 'location-search-mobile' : 'location-search'}
            className={isMobile ? css.mobileInputRoot : desktopInputRootClass}
            iconClassName={isMobile ? css.mobileIcon : css.desktopIcon}
            inputClassName={isMobile ? css.mobileInput : css.desktopInput}
            predictionsClassName={isMobile ? css.mobilePredictions : css.desktopPredictions}
            predictionsAttributionClassName={isMobile ? css.mobilePredictionsAttribution : null}
            placeholder={intl.formatMessage({ id: 'TopbarSearchForm.placeholder' })}
            closeOnBlur={!isMobile}
            inputRef={inputRef}
            input={{ ...restInput, onChange: searchOnChange }}
            meta={meta}
            submitButton={SubmitButton}
            ariaLabel={intl.formatMessage({ id: 'TopbarDesktop.screenreader.search' })}
          />
        );
      }}
    />
  );
};

/**
 * The main search form for the Topbar.
 *
 * @component
 * @param {Object} props
 * @param {string?} props.className add more style rules in addition to components own css.root
 * @param {string?} props.rootClassName overwrite components own css.root
 * @param {string?} props.desktopInputRoot root class for desktop form input
 * @param {Function} props.onSubmit
 * @param {boolean} props.isMobile
 * @param {Object} props.appConfig
 * @param {Function} [props.onSuggestionsOpenChange]
 * @returns {JSX.Element} search form element
 */
const TopbarSearchForm = props => {
  const searchInpuRef = useRef(null);
  const submitRef = useRef(null);
  const intl = useIntl();
  const { appConfig, onSubmit, onSuggestionsOpenChange, ...restOfProps } = props;

  const onChange = location => {
    if (!isMainSearchTypeKeywords(appConfig) && location.selectedPlace) {
      // Note that we use `onSubmit` instead of the conventional
      // `handleSubmit` prop for submitting. We want to autosubmit
      // when a place is selected, and don't require any extra
      // validations for the form.
      onSubmit({ location });
      // blur search input to hide software keyboard
      searchInpuRef?.current?.blur();
    }
  };

  const onKeywordSubmit = () => {
    submitRef.current?.submit();
  };

  const onLocationSubmit = values => {
    // Allow submit button click for an empty location search form
    if (!isMainSearchTypeKeywords(appConfig)) {
      onSubmit({ location: values.location });
    }
  };

  const isKeywordsSearch = isMainSearchTypeKeywords(appConfig);
  const submit = isKeywordsSearch ? onKeywordSubmit : onLocationSubmit;
  return (
    <FinalForm
      {...restOfProps}
      onSubmit={submit}
      render={formRenderProps => {
        const {
          rootClassName,
          className,
          desktopInputRoot,
          isMobile = false,
          handleSubmit,
          values,
          form,
        } = formRenderProps;
        const classes = classNames(rootClassName, className);
        const desktopInputRootClass = desktopInputRoot || css.desktopInputRoot;

        const keywordSearchWrapperClasses = classNames(
          css.keywordSearchWrapper,
          isMobile ? css.mobileInputRoot : [desktopInputRootClass, css.desktopKeywordSearch]
        );

        return (
          <Form className={classes} onSubmit={handleSubmit} enforcePagePreloadFor="SearchPage">
            {isKeywordsSearch ? (
              <KeywordSearchField
                keywordSearchWrapperClasses={keywordSearchWrapperClasses}
                iconClass={classNames(isMobile ? css.mobileIcon : css.desktopIcon || css.icon)}
                intl={intl}
                isMobile={isMobile}
                inputRef={searchInpuRef}
                submitRef={submitRef}
                value={values.keywords || ''}
                onChange={next => form.change('keywords', next)}
                onSuggestionsOpenChange={onSuggestionsOpenChange}
                onSubmitKeywords={keywords => {
                  onSubmit({ keywords });
                  searchInpuRef?.current?.blur();
                }}
                onSubmitSeller={seller => {
                  onSubmit({ seller });
                  form.restart({ keywords: undefined });
                  searchInpuRef?.current?.blur();
                }}
              />
            ) : (
              <LocationSearchField
                desktopInputRootClass={desktopInputRootClass}
                intl={intl}
                isMobile={isMobile}
                inputRef={searchInpuRef}
                onLocationChange={onChange}
              />
            )}
          </Form>
        );
      }}
    />
  );
};

export default TopbarSearchForm;
